import MarketSignalStreamSelector from '@/modules/market-signals/components/market-signals/market-signal-stream-selector';
import { useContext, useEffect, useState } from 'react';
import { MarketSignalStream } from '@/modules/market-signals/model/market-signals/market-signal-stream';
import {
  Button,
  FormControl,
  FormGroup,
  FormLabel,
  FormText,
  InputGroup,
  Table,
} from 'react-bootstrap';
import { Page, PageControl } from '@/shared/pagination/export';
import { MarketSignalInfoSource } from '@/modules/market-signals/model/market-signals/market-signal-info-source';
import marketSignalApi from '@/modules/market-signals/api/market-signal-api';
import { CheckboxRow } from '@/shared/components/export';
import { Menu } from '@/core/menu/export';
import MarketSignalSourceBadge from '@/modules/market-signals/components/market-signals/market-signal-source-badge';
import FormRange from 'react-bootstrap/FormRange';
import { MarketSignalContext } from '@/modules/market-signals/contexts/market-signal-context';

export default function MarketSignalStreamConfig({ onClose }: { onClose: () => void }) {
  const [stream, setStream] = useState<MarketSignalStream>();
  const { streams, setStreams } = useContext(MarketSignalContext);

  const [sources, setSources] = useState<Page<MarketSignalInfoSource>>();
  const [page, setPage] = useState<number>(0);

  useEffect(() => {
    marketSignalApi.getInfoSources(page).then(setSources);
  }, [page]);

  const addSource = (s: string) => {
    if (stream) {
      if (stream.includedSources.includes(s))
        stream.includedSources = stream.includedSources.filter(value => value !== s);
      else stream.includedSources.push(s);
      setStream({ ...stream });
    }
  };

  const save = async () => {
    if (stream) {
      const savedStream = await marketSignalApi.saveStream(stream);

      const updatedStreams = streams.map(s => (s._id === savedStream._id ? savedStream : s));
      setStreams(updatedStreams);
    }

    onClose();
  };

  const setName = (s: string) => {
    if (stream) {
      stream.name = s;

      if (stream._id === undefined) stream.code = s.replaceAll(/\s+/g, '-').toLowerCase();

      setStream({ ...stream });
    }
  };

  const setNlpPrompt = (s: string) => {
    stream && setStream({ ...stream, nlpPrompt: s });
  };

  const setTopicAmount = (s: string) => {
    stream && setStream({ ...stream, topicAmount: parseInt(s) });
  };

  const includesAll = () => {
    return stream?.includedSources.includes('*');
  };

  const newStream = () => {
    setStream({
      _id: undefined,
      code: '',
      name: '',
      includedSources: [],
      nlpPrompt: '',
      topicAmount: 10,
    });
  };

  return (
    <div>
      <FormGroup className={'mb-3 border-bottom pb-3'}>
        <FormLabel>Stream</FormLabel>
        <InputGroup>
          <MarketSignalStreamSelector onStreamSelect={setStream} />
          <Button variant={'secondary'} onClick={newStream}>
            New
          </Button>
        </InputGroup>
      </FormGroup>

      <FormGroup className={'mb-3'}>
        <FormLabel>Id</FormLabel>
        <FormControl value={stream?._id || ''} disabled={true} />
      </FormGroup>

      <FormGroup className={'mb-3'}>
        <FormLabel>Code</FormLabel>
        <FormControl value={stream?.code} disabled={true} />
      </FormGroup>

      <FormGroup className={'mb-3'}>
        <FormLabel>Name</FormLabel>
        <FormControl value={stream?.name} onChange={event => setName(event.target.value)} />
      </FormGroup>

      <FormGroup className={'mb-3'}>
        <FormLabel>Topic Instructions</FormLabel>
        <FormControl
          value={stream?.nlpPrompt || ''}
          onChange={event => setNlpPrompt(event.target.value)}
        />
        <FormText>
          Used to control how the summarized topic titles and their descriptions are created.
        </FormText>
      </FormGroup>

      <FormGroup className={'mb-3'}>
        <FormLabel>Topic Amount: {stream?.topicAmount}</FormLabel>
        <div>
          <FormRange
            value={stream?.topicAmount}
            min={1}
            max={10}
            step={1}
            onChange={event => setTopicAmount(event.target.value)}
          ></FormRange>
        </div>
      </FormGroup>

      <CheckboxRow
        className="mb-3"
        checked={!!includesAll()}
        onChange={() => addSource('*')}
        label="Includes All"
      />
      {includesAll() || (
        <>
          <FormLabel>Stream Sources</FormLabel>

          <Table className={'small mb-3'} size={'sm'}>
            <thead></thead>
            <tbody>
              {stream?.includedSources.map(value => {
                const [scheme, code] = value.split(':');

                return (
                  <tr>
                    <td>
                      <code>@{code}</code>
                    </td>
                    <td>
                      <MarketSignalSourceBadge source={scheme} />
                    </td>
                    <td className={'text-end'}>
                      <a
                        href={'#a'}
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          addSource(value);
                        }}
                      >
                        Remove
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <FormLabel>Available Sources</FormLabel>

          <PageControl offset={page} onChange={offset => setPage(offset)} menu={<Menu />} />
          <Table>
            <thead>
              <tr>
                <th>Included</th>
                <th>Name</th>
                <th>ID</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {sources?.content.map(value => (
                <tr>
                  <td>
                    <CheckboxRow
                      checked={!!stream?.includedSources.includes(`${value.scheme}:${value.code}`)}
                      onChange={() => addSource(`${value.scheme}:${value.code}`)}
                    />
                  </td>
                  <td>{value.name}</td>
                  <td>
                    <code>@{value.code}</code>
                  </td>
                  <td>
                    <MarketSignalSourceBadge source={value.scheme} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      <div className={'d-flex w-100'}>
        <div className={'justify-content-end'}>
          <Button onClick={save} variant={'success'} className={'text-end'}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
